import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './SearchVisualizers.module.css';
import { generateSortedArray } from '../../lib/arrayGenerator';

const BINARY_SEARCH_CODE = `function binarySearch(arr, target) {
  let left = 0;
  let right = arr.length - 1;
  while (left <= right) {
    const mid = Math.floor((left + right) / 2);
    if (arr[mid] === target) {
    } else if (arr[mid] < target) {
      left = mid + 1;
    } else {
      right = mid - 1;
    }
  }
  return -1; 
}`;

const BinarySearch: React.FC = () => {
  const navigate = useNavigate();
  const [array, setArray] = useState(generateSortedArray(15, 100));
  const [target, setTarget] = useState(array[Math.floor(Math.random() * array.length)]);
  const [highlightedIndices, setHighlightedIndices] = useState<number[]>([]);
  const [foundIndex, setFoundIndex] = useState<number | null>(null);
  const [highlightedLine, setHighlightedLine] = useState<number | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const stopSearch = useRef(false);

  const delay = (ms: number) => new Promise(res => setTimeout(res, ms));

  const handleStop = useCallback(() => {
    stopSearch.current = true;
    setIsSearching(false);
    setHighlightedIndices([]);
    setFoundIndex(null);
    setHighlightedLine(null);
  }, []);

  const handleRandomize = useCallback(() => {
    handleStop();
    const newArray = generateSortedArray(15, 100);
    setArray(newArray);
    setTarget(newArray[Math.floor(Math.random() * newArray.length)]);
  }, [handleStop]);

  useEffect(() => {
    handleRandomize();
    return () => {
      stopSearch.current = true;
    };
  }, [handleRandomize]);

  const runBinarySearch = async () => {
    stopSearch.current = false;
    setHighlightedIndices([]);
    setFoundIndex(null);
    setIsSearching(true);
    
    let left = 0;
    let right = array.length - 1;
    let found = false;

    while (left <= right) {
      if (stopSearch.current) return setIsSearching(false);
      const mid = Math.floor((left + right) / 2);
      setHighlightedIndices([left, right, mid]);
      setHighlightedLine(5);
      await delay(500);

      if (stopSearch.current) return setIsSearching(false);
      setHighlightedLine(6);
      if (array[mid] === target) {
        setHighlightedIndices([]);
        setFoundIndex(mid);
        setHighlightedLine(7);
        found = true;
        await delay(500);
        break;
      } else if (array[mid] < target) {
        setHighlightedLine(9);
        left = mid + 1;
      } else {
        setHighlightedLine(11);
        right = mid - 1;
      }
      await delay(500);
    }

    if (!found && !stopSearch.current) {
      setHighlightedLine(14);
    }
    setIsSearching(false);
  };

  const handleGoBack = () => navigate(-1);
  
  return (
    <div className={styles.container}>
      <button onClick={handleGoBack} className={styles.returnButton}>
        &larr; Return
      </button>
      <h1 className={styles.title}>Binary Search</h1>

      <div className={styles.description}>
        <p>Binary search is an efficient algorithm for finding an item from a <b>sorted</b> list of items. It works by repeatedly dividing the search interval in half.</p>
      </div>
      <div className={styles.useCase}>
        <h3>When to use it?</h3>
        <ul>
          <li>When the array is <b>sorted</b> and <b>large</b>.</li>
          <li>When you need a <b>faster search time</b> than linear search.</li>
        </ul>
      </div>
      <div className={styles.timeComplexity}>
        <h3>Time Complexity</h3>
        <p>Binary search has a time complexity of <b>O(log n)</b>.</p>
      </div>

      <div className={styles.visualization}>
        {array.map((value, index) => (
          <div
            key={index}
            className={`${styles.bar} ${highlightedIndices.includes(index) ? styles.searching : ''} ${foundIndex === index ? styles.found : ''}`}
          >
            {value}
          </div>
        ))}
      </div>
      <div className={styles.controls}>
        <button onClick={runBinarySearch} disabled={isSearching}>Run Search</button>
        <button onClick={handleRandomize} disabled={isSearching}>Randomize</button>
        <button onClick={handleStop} disabled={!isSearching}>Stop</button>
        <input
          type="number"
          value={target}
          onChange={(e) => setTarget(parseInt(e.target.value))}
          placeholder="Enter target value"
        />
      </div>
      <div className={styles.codeDisplay}>
        {BINARY_SEARCH_CODE.split('\n').map((line, index) => (
          <div
            key={index}
            className={`${styles.codeLine} ${highlightedLine === index + 1 ? styles.highlightedLine : ''}`}
          >
            {line}
          </div>
        ))}
      </div>
    </div>
  );
};

export default BinarySearch;